import isObject from 'lodash/isObject';

export const STASH_BRIDGE_NAME = 'stashBridge';

type BridgeMessage = {
	action: string;
	payload: Record<string, unknown>;
};

type AndroidBridge = {
	receiveMessageFromJS: (message: string) => void;
};

type IOSMessageHandler = {
	postMessage: (message: BridgeMessage) => void;
};

type WebkitWindow = Window & {
	webkit?: {
		messageHandlers?: Record<string, IOSMessageHandler | undefined>;
	};
};

// NOTE: stashPlatformConstant is duplicated in server/constants/native-auth.js
// This duplication exists because client (ESM) and server (CommonJS) use different module systems.
// If values change, ensure both files are updated to stay in sync.
export const stashPlatformConstant = {
	ANDROID: 'android',
	IOS: 'ios',
};

// NOTE: cookieNames is duplicated in server/constants/native-auth.js
// This duplication exists because client (ESM) and server (CommonJS) use different module systems.
// If values change, ensure both files are updated to stay in sync.
export const cookieNames = {
	STASH_ACCESS_TOKEN: 'stashAccessToken',
	STASH_REFRESH_TOKEN: 'stashRefreshToken',
	STASH_UUID: 'stashUUID',
	NATIVE_PLATFORM: 'nativePlatform',
	NATIVE_APP_VERSION: 'nativeAppVersion',
	NATIVE_APP_ID: 'nativeAppID',
	NATIVE_THEME: 'nativeTheme',
};

const getOSPostMessageFn = (
	bridgeName: string = STASH_BRIDGE_NAME
): ((msg: BridgeMessage) => void) => {
	// Guard for non-browser environments (SSR, tests)
	if (typeof window === 'undefined') {
		return () => {
			// No-op in non-browser environments
		};
	}

	// Android Webviews
	const androidBridge = (window as unknown as Record<string, AndroidBridge | undefined>)[
		bridgeName
	];
	if (androidBridge) {
		const bridge = androidBridge.receiveMessageFromJS.bind(androidBridge);
		return (msg: BridgeMessage) => bridge(JSON.stringify(msg)); // JSON string for android
	}

	// iOS Webviews
	const handler = (window as WebkitWindow)?.webkit?.messageHandlers?.[bridgeName];
	if (handler && typeof handler.postMessage === 'function') {
		return handler.postMessage.bind(handler);
	}

	return (msg: BridgeMessage) => {
		console.group(`Bridge message received`);
		console.log(msg);
		console.groupEnd();
		if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent('appetizerMessage', {
					detail: msg,
				})
			);
		}
	};
};

export const createMessageDispatcher = (
	bridge: string = STASH_BRIDGE_NAME
): ((msg: BridgeMessage) => void) => {
	const stashBridgePostMessage = getOSPostMessageFn(bridge);
	return (msg: BridgeMessage): void => {
		if (!isObject(msg) || typeof msg.action !== 'string' || !isObject(msg.payload)) {
			throw new Error('tried sending a message with improper format');
		}
		stashBridgePostMessage(msg);
	};
};

export const sendMessage = createMessageDispatcher();
